/**
 * Draft Storage Utility
 * Handles persistence of form drafts to localStorage with expiration support
 */

interface DraftEntry<T> {
  data: T
  timestamp: number
  page: string
}

const DRAFT_STORAGE_KEY = 'sidego_form_drafts'
const DRAFT_EXPIRATION_DAYS = 7
const DRAFT_EXPIRATION_MS = DRAFT_EXPIRATION_DAYS * 24 * 60 * 60 * 1000

/**
 * Get all stored drafts from localStorage
 */
function getAllDrafts(): Record<string, DraftEntry<any>> {
  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch (error) {
    console.error('Failed to retrieve drafts from localStorage:', error)
    return {}
  }
}

/**
 * Check if a draft has expired
 */
function isDraftExpired(timestamp: number): boolean {
  return Date.now() - timestamp > DRAFT_EXPIRATION_MS
}

/**
 * Save draft to localStorage
 * @param page - Identifier for the page (e.g., 'login', 'register')
 * @param data - Form data to save
 */
export function saveDraft<T extends Record<string, any>>(
  page: string,
  data: T
): void {
  try {
    const drafts = getAllDrafts()
    
    // Validate page identifier
    if (!page || typeof page !== 'string') {
      console.warn('Invalid page identifier for draft save')
      return
    }

    // Store draft with metadata
    drafts[page] = {
      data,
      timestamp: Date.now(),
      page
    }

    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts))
  } catch (error) {
    console.error('Failed to save draft to localStorage:', error)
  }
}

/**
 * Retrieve draft from localStorage
 * @param page - Identifier for the page
 * @returns Draft data if exists and not expired, null otherwise
 */
export function getDraft<T extends Record<string, any>>(page: string): T | null {
  try {
    const drafts = getAllDrafts()
    const draft = drafts[page]

    if (!draft) {
      return null
    }

    // Check if draft has expired
    if (isDraftExpired(draft.timestamp)) {
      // Clean up expired draft
      deleteDraft(page)
      return null
    }

    return draft.data as T
  } catch (error) {
    console.error('Failed to retrieve draft from localStorage:', error)
    return null
  }
}

/**
 * Delete a specific draft
 * @param page - Identifier for the page
 */
export function deleteDraft(page: string): void {
  try {
    const drafts = getAllDrafts()
    delete drafts[page]
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts))
  } catch (error) {
    console.error('Failed to delete draft from localStorage:', error)
  }
}

/**
 * Clear all drafts (typically called after successful submission)
 */
export function clearAllDrafts(): void {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY)
  } catch (error) {
    console.error('Failed to clear all drafts:', error)
  }
}

/**
 * Clean up expired drafts
 */
export function cleanupExpiredDrafts(): void {
  try {
    const drafts = getAllDrafts()
    let hasChanges = false

    Object.keys(drafts).forEach((page) => {
      if (isDraftExpired(drafts[page].timestamp)) {
        delete drafts[page]
        hasChanges = true
      }
    })

    if (hasChanges) {
      if (Object.keys(drafts).length === 0) {
        localStorage.removeItem(DRAFT_STORAGE_KEY)
      } else {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts))
      }
    }
  } catch (error) {
    console.error('Failed to cleanup expired drafts:', error)
  }
}

/**
 * Get storage size in bytes
 */
export function getDraftStorageSize(): number {
  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY)
    return stored ? new Blob([stored]).size : 0
  } catch (error) {
    console.error('Failed to calculate draft storage size:', error)
    return 0
  }
}